import { Component, OnInit, inject, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { BusySpinnerComponent } from "@components/busy/busy-spinner/busy-spinner.component";
import { ButtonComponent } from "@components/button/button.component";
import { HeaderComponent } from "@components/header/header.component";
import { InputNumberComponent } from "@components/input-number/input-number.component";
import { InputComponent } from "@components/input/input.component";
import { MapLayersComponent } from "@components/map/components/menu/map-layers/map-layers.component";
import { MapMeasureComponent } from "@components/map/components/menu/map-measure/map-measure.component";
import { MapComponent } from "@components/map/map.component";
import { RadiobuttonGroupComponent } from "@components/radiobuttongroup/radiobuttongroup.component";
import { Observable, of } from "rxjs";
import { IMapComponent } from "~app/common/components/map/map.models";
import { IMapGeometryWicket, IMapSettings } from "~app/common/components/map/map.settings";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ArbetsorderAtgardDTO } from "~app/common/models/arbetsorder/atgard/ArbetsorderAtgardDTO";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { VirkeskopsatgardDTO } from "~app/common/models/virkeskop/VirkeskopKontraktAvtalResponseDTO";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { DirtyTabComponent } from "~app/features/regelverk-viol/dirtyTabComponent";
import { SystemadministrationService } from "../../systemadministration.service";

@Component({
    selector: "rask-sysadm-geometri",
    templateUrl: "./sysadm-geometri.component.html",
    styleUrls: ["./sysadm-geometri.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        HeaderComponent,
        InputComponent,
        FormsModule,
        RadiobuttonGroupComponent,
        ButtonComponent,
        BusySpinnerComponent,
        InputNumberComponent,
        MapComponent,
        MapMeasureComponent,
        MapLayersComponent
    ]
})
export class SysadmGeometriComponent extends DirtyTabComponent implements OnInit {
    bufferMeters = 0.01;
    errorText: string;
    feature = "virkeskop";
    isAtgardLoaded = false;
    mapsettings: IMapSettings;
    searchText: any;
    simplifyMeters = 0.01;
    working = false;

    private readonly modalService = inject(RaskModalService);
    private readonly sysAdmService = inject(SystemadministrationService);

    private atgard: any;
    private map: IMapComponent;

    constructor() {
        const dirtyFormService = inject(DirtyFormService);

        super(dirtyFormService);
        this.mapsettings = {
            id: "adm",
            height: "100%",
            zoom: 0,
            zoomButtons: true,
            onMapReady: (map) => this.onMapReady(map),
            baselayers: [
                {
                    id: "metriabakgrund",
                    visible: true
                },
                {
                    id: "metriaorto",
                    visible: true
                }
            ],
            drawLayers: [],
            featurelayers: [
                {
                    id: "Atgarder",
                    hideInLayerList: true,
                    visible: true
                }
            ],
            scaleBar: true,
            measure: true,
            coords: true,
            zoomTo: true
        } as IMapSettings;
    }

    canSave() {
        // Tom, ingen användning här
    }

    isDirtyCheck(): boolean {
        return false;
    }

    makeBuffer() {
        this.makeValid("buffer", this.bufferMeters);
    }

    makeSimplify() {
        this.makeValid("simplify", this.simplifyMeters);
    }

    makeValid(method: string, meters: number): void {
        this.working = true;
        const sub = (Math.abs(meters) > 0.5) ? this.modalService.confirm(new ModalSettings(method,`${meters} meter: Är du säker?`)) : of(null);
        sub.subscribe(
            {
                next: result => {
                    if (result === ConfirmDialogResponse.save) {
                        this.sysAdmService.makeValid(this.atgard.id, this.feature, method, meters).subscribe(
                            {
                                next: () => {
                                    setTimeout(() => {
                                        // eslint-disable-next-line sonarjs/no-nested-functions
                                        this.getAtgard(this.feature, this.searchText, (atg) => {
                                            this.initAtgard(atg, false, [
                                                200,
                                                255,
                                                200,
                                                0.3
                                            ]);
                                            this.working = false;
                                        });
                                    }, 3000);
                                },
                                error: (error) => {
                                    this.working = false;
                                    this.errorText = `Fel inträffade. Kunde inte göra '${method}' på geometrin. ${this.atgard.geometri}`;
                                }
                            }
                        );
                    }
                },
                complete: () => {
                    this.working = false;
                }
            }
        );
    }

    ngOnInit(): void {
        this.reset();
    }

    onMapReady(map: IMapComponent): void {
        this.map = map;
    }

    restoreChanges() {
        // Tom, ingen användning här
    }

    saveChanges() {
        // Tom, ingen användning här
    }

    search(): void {
        this.reset();
        this.getAtgard(this.feature, this.searchText, (atg) => {
            this.initAtgard(atg, true, [
                200,
                200,
                255,
                0.3
            ]);
        });
    }

    private getAtgard(feature: string, beteckning: string, callback: (atg: VirkeskopsatgardDTO | ArbetsorderAtgardDTO) => void): void {
        this.working = true;
        this.getAtgardByFeature(feature, beteckning).subscribe(
            {
                next: (atgard) => {
                    if (atgard) {
                        this.working = false;
                        callback(atgard);
                    } else {
                        this.working = false;
                        this.errorText = `Kunde inte hitta åtgärd med beteckning '${this.searchText}'`;
                    }
                },
                error: () => {
                    this.working = false;
                    this.errorText = "Fel inträffade";
                }
            }
        );
    }

    private getAtgardByFeature(feature: string, beteckning: string): Observable<VirkeskopsatgardDTO | ArbetsorderAtgardDTO> {
        if (feature === "virkeskop") {
            return this.sysAdmService.getVirkeskopsatgard(beteckning);
        } else if (feature === "arbetsorder") {
            return this.sysAdmService.getArbetsordersatgard(beteckning);
        }
        return undefined;
    }

    private initAtgard(atg: VirkeskopsatgardDTO | ArbetsorderAtgardDTO, clear: boolean, color: any): void {
        if (!ObjectUtils.hasValue(atg.geometri)) {
            this.errorText = `Åtgärden ${atg.beteckning} saknar geometri.`;
            return;
        }
        this.map.preview([this.mapToRaskGeometry(atg)], color, clear, null, true);
        this.isAtgardLoaded = true;
        this.atgard = atg;
    }

    private mapToRaskGeometry(atg: VirkeskopsatgardDTO | ArbetsorderAtgardDTO): IMapGeometryWicket {
        return {
            id: atg.id,
            wkt: atg.geometri,
            visible: true,
            tag: atg
        };
    }

    private reset(): void {
        this.atgard = null;
        this.isAtgardLoaded = false;
        this.errorText = "";
        this.working = false;
        this.map?.clearPreview();
    }
}
